import { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Image as ImageIcon, Sparkles, Loader2, RefreshCw, Copy, Wand2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import StimulusCard from "@/components/StimulusCard";
import StarterLibraryDialog from "@/components/StarterLibraryDialog";
import CategoryEditorDialog from "@/components/CategoryEditorDialog";
import { VERBAL_OPERANTS, OPERANT_COLORS } from "@/lib/operants";
import { CARDS } from "@/constants/testIds";

async function fileToDataUrl(file, maxSize = 700) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const initialForm = {
  label: "",
  verbal_operant: "Tact",
  card_type: "photo",
  image_data_url: "",
  imageUrl: "",
  prompt_text: "",
  pronunciation: "",
  template: "photo",
  stimulus_style: "real",
  concept_id: "",
};

export default function CardsPage() {
  const [cards, setCards] = useState([]);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [form, setForm] = useState(initialForm);

  const load = async () => {
    try {
      // Fast path — skip inline base64 images (was ballooning to ~5 MB on 30+ card libraries
      // and tripping Cloudflare). Fetch images in a second batch call and merge in.
      const r = await http.get("/cards");
      const list = r.data || [];
      setCards(list);
      const photoIds = list.filter((c) => (c.card_type || "photo") === "photo").map((c) => c.id);
      if (photoIds.length === 0) return;
      const imgResp = await http.post("/cards/images-by-ids", { ids: photoIds });
      const map = imgResp.data?.images || {};
      setCards((prev) => prev.map((c) => (map[c.id] ? { ...c, image_data_url: map[c.id] } : c)));
    } catch (e) {
      /* ignore — page shows empty state */
      void e;
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = cards.filter((c) => {
    if (filter !== "all" && c.verbal_operant !== filter) return false;
    if (typeFilter !== "all" && (c.card_type || "photo") !== typeFilter) return false;
    return true;
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Please choose an image under 8MB"); return; }
    const url = await fileToDataUrl(file);
    setForm((f) => ({ ...f, image_data_url: url, imageUrl: "" }));
  };

  const generateAI = async () => {
    if (!form.label.trim()) {
      toast.error("Enter a label first (e.g. 'a red apple') so we know what to draw");
      return;
    }
    setGenLoading(true);
    try {
      const r = await http.post("/cards/generate-image", {
        prompt: form.label,
        style: form.stimulus_style || "real",
      });
      setForm((f) => ({ ...f, image_data_url: r.data.image_data_url, imageUrl: "" }));
      toast.success(`${form.stimulus_style === "illustration" ? "Illustration" : "Photo"} generated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally {
      setGenLoading(false);
    }
  };

  const [dupWarn, setDupWarn] = useState(null); // {message, submitAgain}
  const save = async (e) => {
    e.preventDefault();
    const image_data_url =
      form.card_type === "photo" ? (form.image_data_url || form.imageUrl) : "";
    if (form.card_type === "photo" && !image_data_url) {
      toast.error("Add an image (upload, paste URL, or generate)");
      return;
    }
    const payload = {
      label: form.label.trim(),
      verbal_operant: form.verbal_operant,
      card_type: form.card_type,
      image_data_url,
      prompt_text: form.prompt_text?.trim() || "",
      pronunciation: form.pronunciation?.trim() || "",
    };
    const submit = async (force = false) => {
      setSaving(true);
      try {
        await http.post(`/cards${force ? "?force=true" : ""}`, payload);
        toast.success("Card added");
        setForm(initialForm);
        setOpen(false);
        setDupWarn(null);
        load();
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (err.response?.status === 409 && detail?.code === "duplicate") {
          setDupWarn({ message: detail.message, submit: () => submit(true) });
        } else {
          toast.error(formatApiError(detail) || "Failed to save");
        }
      } finally {
        setSaving(false);
      }
    };
    submit(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this card and unassign from all learners?")) return;
    try {
      await http.delete(`/cards/${id}`);
      toast.success("Card removed");
      load();
    } catch { toast.error("Failed"); }
  };

  const [regenId, setRegenId] = useState(null);
  const [pairId, setPairId] = useState(null);
  const regenerate = async (id) => {
    setRegenId(id);
    try {
      await http.post(`/cards/${id}/regenerate-image`);
      toast.success("Image regenerated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Regenerate failed");
    } finally {
      setRegenId(null);
    }
  };

  const generatePair = async (id) => {
    setPairId(id);
    try {
      const r = await http.post(`/cards/${id}/generate-pair`);
      toast.success(`Generated ${r.data.stimulus_style} pair for "${r.data.label}"`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Pair generation failed");
    } finally {
      setPairId(null);
    }
  };

  const duplicateToOperant = async (card, operant) => {
    try {
      await http.post("/cards/duplicate", { card_id: card.id, verbal_operant: operant });
      toast.success(`Duplicated as ${operant}`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail?.message || formatApiError(detail) || "Failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-6xl">
      <PageHeader
        eyebrow="Stimuli"
        title="Card Library"
        description="Photos, text prompts & imitation cues — color-coded by verbal operant."
        actions={
          <div className="flex gap-2 flex-wrap">
            <StarterLibraryDialog onImported={load} />
            <CategoryEditorDialog onSaved={load} />
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(initialForm); }}>
              <DialogTrigger asChild>
                <Button className="rounded-full h-11 px-5" data-testid={CARDS.addBtn}>
                  <Plus className="w-4 h-4 mr-2" /> Add card
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display">New stimulus card</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-4">
                  {/* Template quick-picker */}
                  <div>
                    <Label>Template</Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1.5">
                      {[
                        { id: "photo",       label: "Photo",       card_type: "photo",     operant: "Tact" },
                        { id: "listener",    label: "Listener",    card_type: "text",      operant: "Listener" },
                        { id: "wh",          label: "Wh Question", card_type: "text",      operant: "Intraverbal" },
                        { id: "fillin",      label: "Fill-in",     card_type: "text",      operant: "Intraverbal" },
                        { id: "echoic",      label: "Echoic",      card_type: "text",      operant: "Echoic" },
                        { id: "imitation",   label: "Imitation",   card_type: "imitation", operant: "Imitation" },
                        { id: "text",        label: "Textual",     card_type: "text",      operant: "Textual" },
                      ].map((t) => {
                        const active = form.template === t.id ||
                          (!form.template && form.card_type === t.card_type && form.verbal_operant === t.operant);
                        return (
                          <button
                            type="button"
                            key={t.id}
                            onClick={() => setForm({
                              ...form,
                              template: t.id,
                              card_type: t.card_type,
                              verbal_operant: t.operant,
                            })}
                            data-testid={`template-${t.id}`}
                            className={`h-10 rounded-xl border text-xs sm:text-sm font-semibold transition-colors ${
                              active
                                ? "bg-slate-900 text-white border-slate-900"
                                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                            }`}
                          >
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label>Label</Label>
                    <Input
                      required data-testid={CARDS.labelInput}
                      value={form.label}
                      onChange={(e) => setForm({ ...form, label: e.target.value })}
                      placeholder={
                        form.template === "wh"       ? "e.g. 'What flies in the sky?'"
                        : form.template === "fillin" ? "e.g. 'Twinkle twinkle little ___'"
                        : form.template === "echoic" ? "e.g. 'ah' or 'buh'"
                        : form.template === "listener" ? "e.g. 'Touch dog'"
                        : form.card_type === "text"       ? "e.g. 'What flies in the sky?' or 'ah'"
                        : form.card_type === "imitation"  ? "e.g. 'Clap hands'"
                        : "e.g. Dog"
                      }
                      className="mt-1.5 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>Verbal operant</Label>
                    <Select value={form.verbal_operant}
                      onValueChange={(v) => setForm({ ...form, verbal_operant: v })}>
                      <SelectTrigger className="mt-1.5 rounded-xl" data-testid={CARDS.operantSelect}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VERBAL_OPERANTS.map((op) => (
                          <SelectItem key={op} value={op}>
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full" style={{ background: OPERANT_COLORS[op].border }} />
                              {op}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {form.card_type === "photo" && (
                    <div>
                      <Label>Image</Label>
                      <div className="mt-1.5 flex gap-2 mb-2" data-testid="stimulus-style-toggle">
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, stimulus_style: "real" })}
                          data-testid="style-real"
                          className={`flex-1 h-9 rounded-lg text-xs font-semibold border transition-colors ${
                            (form.stimulus_style || "real") === "real"
                              ? "bg-slate-900 text-white border-slate-900"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          Real photo
                        </button>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, stimulus_style: "illustration" })}
                          data-testid="style-illustration"
                          className={`flex-1 h-9 rounded-lg text-xs font-semibold border transition-colors ${
                            form.stimulus_style === "illustration"
                              ? "bg-slate-900 text-white border-slate-900"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          Illustration
                        </button>
                      </div>
                      <div className="rounded-xl border-2 border-dashed border-slate-200 p-4 bg-slate-50">
                        {form.image_data_url || form.imageUrl ? (
                          <img
                            src={form.image_data_url || form.imageUrl}
                            alt="preview"
                            className="w-full h-40 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="h-24 flex items-center justify-center text-slate-400 text-sm">
                            <ImageIcon className="w-4 h-4 mr-2" /> Upload, paste URL, or generate with AI
                          </div>
                        )}
                        <div className="flex gap-2 mt-3">
                          <label className="flex-1 cursor-pointer">
                            <input type="file" accept="image/*" onChange={handleFile}
                              className="hidden" data-testid={CARDS.fileInput} />
                            <div className="w-full text-sm text-center h-10 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center gap-2">
                              <Upload className="w-3.5 h-3.5" /> Upload
                            </div>
                          </label>
                          <button
                            type="button" onClick={generateAI} disabled={genLoading}
                            data-testid="ai-generate-button"
                            className="flex-1 text-sm h-10 rounded-lg bg-slate-900 text-white hover:bg-slate-800 flex items-center justify-center gap-2 disabled:opacity-60"
                          >
                            {genLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            {genLoading ? "Generating…" : "Generate AI"}
                          </button>
                        </div>
                        <Input
                          className="mt-2 rounded-lg" placeholder="…or paste image URL"
                          value={form.imageUrl} data-testid={CARDS.urlInput}
                          onChange={(e) => setForm({ ...form, imageUrl: e.target.value, image_data_url: "" })}
                        />
                      </div>
                    </div>
                  )}

                  {form.card_type === "imitation" && (
                    <div>
                      <Label>Instruction for therapist (optional)</Label>
                      <Textarea
                        rows={2}
                        placeholder="e.g. Model: Clap hands 2–3 times for learner to imitate"
                        value={form.prompt_text}
                        onChange={(e) => setForm({ ...form, prompt_text: e.target.value })}
                        className="mt-1.5 rounded-xl"
                        data-testid="prompt-text-input"
                      />
                    </div>
                  )}

                  {form.card_type === "text" && (
                    <>
                      <div>
                        <Label>
                          {form.template === "wh"        ? "Target response"
                          : form.template === "fillin"   ? "Fill-in answer"
                          : form.template === "listener" ? "Delivery instruction"
                          : "Prompt / target response"}
                          {" (optional)"}
                        </Label>
                        <Textarea
                          rows={2}
                          placeholder={
                            form.template === "wh"      ? "Target: bird / plane"
                            : form.template === "fillin" ? "Target: STAR"
                            : form.template === "listener" ? "Instruction: 'Touch dog'. Present in array."
                            : form.verbal_operant === "Echoic" ? "Notes / target"
                            : "Target: ..."
                          }
                          value={form.prompt_text}
                          onChange={(e) => setForm({ ...form, prompt_text: e.target.value })}
                          className="mt-1.5 rounded-xl"
                          data-testid="prompt-text-input"
                        />
                      </div>
                      {(form.verbal_operant === "Echoic" || form.template === "echoic") && (
                        <div>
                          <Label>Pronunciation cue (optional)</Label>
                          <Input
                            placeholder="e.g. Open mouth wide — 'AH'"
                            value={form.pronunciation}
                            onChange={(e) => setForm({ ...form, pronunciation: e.target.value })}
                            className="mt-1.5 rounded-xl"
                            data-testid="pronunciation-input"
                          />
                        </div>
                      )}
                    </>
                  )}

                  <div>
                    <Label>
                      Concept group <span className="text-slate-400 font-normal">(optional, for non-identical matching)</span>
                    </Label>
                    <Input
                      value={form.concept_id || ""}
                      onChange={(e) => setForm({ ...form, concept_id: e.target.value })}
                      placeholder='e.g. "apple" — cards sharing this concept can match non-identically'
                      className="mt-1.5 rounded-xl"
                      data-testid="concept-id-input"
                      maxLength={40}
                    />
                  </div>

                  <DialogFooter>
                    <Button type="submit" className="rounded-full" disabled={saving} data-testid={CARDS.saveBtn}>
                      {saving ? "Saving…" : "Save card"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* Filters — compact single row: type + operants merged */}
      <div className="mb-6 flex flex-wrap items-center gap-1.5">
        {[
          { id: "all", label: "All types" },
          { id: "photo", label: "Photo" },
          { id: "text", label: "Text" },
          { id: "imitation", label: "Imitation" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTypeFilter(t.id)}
            data-testid={`type-filter-${t.id}`}
            className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
              typeFilter === t.id
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
            }`}
          >
            {t.label}
          </button>
        ))}
        <div className="h-4 w-px bg-slate-200 mx-1" aria-hidden />
        <button
          onClick={() => setFilter("all")}
          className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
            filter === "all"
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
          }`}
          data-testid="filter-all"
        >
          All ({cards.length})
        </button>
        {VERBAL_OPERANTS.map((op) => {
          const c = OPERANT_COLORS[op];
          const n = cards.filter((x) => x.verbal_operant === op).length;
          const active = filter === op;
          return (
            <button
              key={op} onClick={() => setFilter(op)}
              className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors"
              style={{
                background: active ? c.border : c.bg,
                color: active ? "white" : c.text,
                borderColor: c.border,
              }}
              data-testid={`filter-${op}`}
            >
              {op} <span className="opacity-70 ml-0.5">{n}</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 sm:p-16 text-center bg-white" data-testid="cards-empty-state">
          <div
            className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center"
            style={filter !== "all"
              ? { background: OPERANT_COLORS[filter]?.bg, color: OPERANT_COLORS[filter]?.text }
              : { background: "#F1F5F9", color: "#475569" }}
          >
            <ImageIcon className="w-6 h-6" />
          </div>
          <h3 className="mt-4 font-display font-bold text-xl">
            {filter === "all"
              ? "No cards yet"
              : `No ${filter} cards yet`}
          </h3>
          <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">
            {filter === "Listener"
              ? "Import Listener Response cards from the Starter library — every Tact has a matching LR twin ready to go."
              : filter === "all"
              ? "Add your first card, generate one with AI, or browse the starter library to import 200+ ready-made stimuli."
              : `Add a ${filter} card manually, or open the Starter library and filter by ${filter} to import pre-built ones.`}
          </p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <StarterLibraryDialog onImported={load} />
          </div>
        </div>
      ) : cardGrid()}

      {/* Duplicate warning dialog */}
      <Dialog open={!!dupWarn} onOpenChange={(o) => { if (!o) setDupWarn(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Card already exists</DialogTitle>
            <DialogDescription>{dupWarn?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setDupWarn(null)} className="rounded-full" data-testid="dup-cancel">
              Cancel
            </Button>
            <Button onClick={() => { dupWarn?.submit?.(); }} className="rounded-full" data-testid="dup-confirm">
              Add anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function cardGrid() { return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {filtered.map((c) => (
            <div key={c.id} className="relative group">
              <StimulusCard card={c} size="md" testId={CARDS.card(c.id)} />
              <div className="absolute top-2 right-2 flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`duplicate-menu-${c.id}`}
                      aria-label="Duplicate to another operant"
                      title="Duplicate to another operant"
                      className="bg-white/95 backdrop-blur border border-slate-200 rounded-full p-2 text-slate-600 hover:text-slate-900"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {VERBAL_OPERANTS.filter((op) => op !== c.verbal_operant).map((op) => (
                      <DropdownMenuItem key={op} onClick={() => duplicateToOperant(c, op)}
                        data-testid={`duplicate-to-${c.id}-${op}`}>
                        <span className="w-2.5 h-2.5 rounded-full mr-2" style={{ background: OPERANT_COLORS[op].border }} />
                        Copy as {op}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {c.card_type === "photo" && (
                  <button
                    onClick={() => regenerate(c.id)}
                    disabled={regenId === c.id}
                    data-testid={`regenerate-${c.id}`}
                    aria-label="Regenerate image with AI"
                    title="Regenerate image with AI"
                    className="bg-white/95 backdrop-blur border border-slate-200 rounded-full p-2 text-slate-600 hover:text-slate-900 disabled:opacity-50"
                  >
                    {regenId === c.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                  </button>
                )}
                {c.card_type === "photo" && (
                  <button
                    onClick={() => generatePair(c.id)}
                    disabled={pairId === c.id}
                    data-testid={`generate-pair-${c.id}`}
                    aria-label={(c.stimulus_style || "real") === "real" ? "Generate illustrated pair" : "Generate real-photo pair"}
                    title={(c.stimulus_style || "real") === "real" ? "Generate illustrated pair (non-identical matching)" : "Generate real-photo pair"}
                    className="bg-white/95 backdrop-blur border border-slate-200 rounded-full p-2 text-fuchsia-600 hover:bg-fuchsia-50 disabled:opacity-50"
                  >
                    {pairId === c.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Wand2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => remove(c.id)}
                  data-testid={CARDS.deleteBtn(c.id)}
                  aria-label="Delete card"
                  className="bg-white/95 backdrop-blur border border-slate-200 rounded-full p-2 text-slate-500 hover:text-rose-600"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      );}
}
